/** Canonical full post input remains bounded by the publishing boundary. Segments are
 * an authorized presentation of that same text, not independent comments or engagement. */
export const POST_TEXT_LIMIT = 5000;
// Operator-reviewed course lessons need room for source extracts, derivations and answers.
// This bound never replaces the ordinary publishing/editing boundary's 5000-character limit.
export const COURSE_POST_TEXT_LIMIT = 24000;
export const POST_PRIMARY_GRAPHEMES = 600;
export const POST_CONTINUATION_GRAPHEMES = 1200;
const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });

/** Split maintained or validated post text without losing whitespace or splitting a
 * grapheme. Prefer paragraph and whitespace boundaries, bounded by grapheme counts.
 * Return [] for empty input; reject nonstrings, oversized input and invalid limits.
 * Ingestion budgets count UTF-16 units. Only trusted callers select maintainedCourse;
 * it is a text bound, never authorization or a user publishing option.
 * No locale, time, database or reader inference influences the result. */
export function splitPostText(
  text,
  {
    primary = POST_PRIMARY_GRAPHEMES,
    continuation = POST_CONTINUATION_GRAPHEMES,
    maintainedCourse = false,
  } = {},
) {
  if (typeof maintainedCourse !== 'boolean') throw new RangeError('Invalid text policy.');
  return splitBoundedText(
    text,
    maintainedCourse ? COURSE_POST_TEXT_LIMIT : POST_TEXT_LIMIT,
    primary,
    continuation,
  );
}

/** Explicit reviewed author-note presentation bound; it grants no parent publishing rights.
 * The owning note validator and adapter establish authority before calling this splitter. */
export function splitAuthorReadingText(text) {
  return splitBoundedText(
    text,
    COURSE_POST_TEXT_LIMIT,
    POST_PRIMARY_GRAPHEMES,
    POST_CONTINUATION_GRAPHEMES,
  );
}
function splitBoundedText(text, maximum, primary, continuation) {
  if (typeof text !== 'string' || text.length > maximum)
    throw new RangeError(`Post text must be a string of at most ${maximum} characters.`);
  if (
    ![primary, continuation].every((n) => Number.isSafeInteger(n) && n > 0 && n <= POST_TEXT_LIMIT)
  )
    throw new RangeError('Segment limits must be positive bounded integers.');
  if (!text) return [];
  const graphemes = [...segmenter.segment(text)].map((part) => part.segment);
  const result = [];
  let offset = 0;
  while (offset < graphemes.length) {
    const limit = result.length ? continuation : primary;
    let end = Math.min(offset + limit, graphemes.length);
    if (end < graphemes.length) {
      // A very early boundary creates tiny fragments; search only the latter half.
      const floor = offset + Math.max(1, Math.floor(limit / 2));
      let whitespace = -1;
      let paragraph = -1;
      for (let index = floor; index < end; index++) {
        if (/\s/u.test(graphemes[index])) whitespace = index + 1;
        if (graphemes[index].includes('\n')) paragraph = index + 1;
      }
      end = paragraph > offset ? paragraph : whitespace > offset ? whitespace : end;
    }
    result.push(graphemes.slice(offset, end).join(''));
    offset = end;
  }
  return result;
}

/** Stable derived identities and order inherit the parent post's authorization and
 * lifecycle. Callers authorize visibility and the maintained-course policy beforehand.
 * The canonical original remains available for lossless author editing. */
export function postContinuations(post, { maintainedCourse = false } = {}) {
  const segments = splitPostText(post.text, { maintainedCourse });
  return {
    text: segments[0] ?? '',
    fullText: post.text,
    authorContinuations: segments.slice(1).map((text, index) => ({
      id: `${post.id}-continuation-${index + 1}`,
      authorId: post.authorId,
      index: index + 1,
      total: segments.length - 1,
      text,
      kind: 'author-continuation',
    })),
  };
}
